import type { VideoStatus } from "./types";

/**
 * native `<video>` 엘리먼트를 다루는 공유 헬퍼. 핸들이 곧 `HTMLVideoElement`(`VideoHandle`)라,
 * 훅 채널이든 단일 `VideoSurface`든 같은 엘리먼트 위에서 이벤트를 정규화하고 안전하게 seek한다.
 * status 머신·autoplay-on-ready·volume 같은 native-event 기반 기능의 토대다(OvenPlayer의 Listener에
 * 해당).
 */

/** 구독 가능한 native video 이벤트 핸들러 맵. 필요한 것만 넣는다. */
export type VideoEventHandlers = Partial<
  Record<
    | "loadedmetadata"
    | "loadeddata"
    | "durationchange"
    | "canplay"
    | "waiting"
    | "stalled"
    | "seeked"
    | "play"
    | "playing"
    | "pause"
    | "ended"
    | "error"
    | "emptied"
    | "resize",
    (event: Event) => void
  >
>;

/** element에 이벤트 리스너를 붙이고, 해제 함수를 반환한다. */
export function subscribeVideoEvents(
  element: HTMLVideoElement,
  handlers: VideoEventHandlers,
): () => void {
  const entries = Object.entries(handlers) as [string, (event: Event) => void][];
  for (const [type, handler] of entries) {
    element.addEventListener(type, handler);
  }
  return () => {
    for (const [type, handler] of entries) {
      element.removeEventListener(type, handler);
    }
  };
}

/** 안전한 seek. 비유한수는 무시하고, 메타데이터 준비 전 거부하는 브라우저를 위해 try로 감싼다. */
export function seekVideo(video: HTMLVideoElement | null, seconds: number): void {
  if (!video || !Number.isFinite(seconds)) return;

  try {
    video.currentTime = Math.max(0, seconds);
  } catch {
    // 일부 브라우저는 메타데이터 준비 전 탐색(seek)을 거부한다.
  }
}

/**
 * status enum의 **단일 정의**: 우선순위·매핑이 여기 한 곳에 있다
 * (error > ended > waiting > buffering > idle > playing/paused). 호출부는 native media의 준비 대기를
 * buffering으로, producer frontier 대기를 waiting으로 정규화한다.
 */
export function deriveStatus(input: {
  hasError: boolean;
  /** 재생 가능한 컨텐츠가 끝까지 도달했는가. */
  ended: boolean;
  /** 메타데이터/길이가 확보돼 재생할 준비가 됐는가(`false`면 idle). */
  ready: boolean;
  /** 실제로 재생 위치가 진행 중인가. */
  playing: boolean;
  /** Native media가 decode/network 준비를 기다리는가. */
  buffering?: boolean;
  /** Producer-paced source가 다음 가용 구간을 기다리는가. */
  waiting?: boolean;
}): VideoStatus {
  if (input.hasError) return "error";
  if (input.ended) return "ended";
  if (input.waiting) return "waiting";
  if (input.buffering) return "buffering";
  if (!input.ready) return "idle";
  return input.playing ? "playing" : "paused";
}

/**
 * 단일 `<video>` 엘리먼트의 status를 native 속성에서 파생한다(단일 controlled VideoSurface용).
 * 엘리먼트를 4불리언으로 환산해 공유 `deriveStatus`에 넘긴다 — 멀티채널 훅도 같은 함수를 쓰므로
 * enum 정의가 어긋나지 않는다. `readyState < 1`(HAVE_METADATA 전)은 idle.
 */
export function deriveVideoStatus(
  video: HTMLVideoElement,
  state: Readonly<{ buffering?: boolean }> = {},
): VideoStatus {
  return deriveStatus({
    hasError: video.error != null,
    ended: video.ended,
    ready: video.readyState >= 1,
    playing: !video.paused,
    buffering: state.buffering,
  });
}
